import { track } from "~/modules/analytics/track";
import { AssetChatUserEvents } from "~/modules/analytics/events/asset_chat";

/** Which chat UI the event came from: the inline per-asset chat or the global dock. */
export type AssetChatSurface = "inline" | "dock";

/**
 * trackOpenFeedbackPopover
 * Tracks the event when a user opens the feedback popover in the chat dock.
 * @param {string} props.view - The dock view the popover was opened from (list / picker / chat).
 */
export function trackOpenFeedbackPopover(props: { view: string }) {
    track(AssetChatUserEvents.OpenFeedbackPopover, props);
}

/**
 * trackSubmitFeedback
 * Tracks the event when a user submits free-text feedback from the chat dock.
 * The feedback text itself is sent as an event property so it can be read in PostHog.
 * @param {string} props.feedback - The free-text feedback the user submitted.
 * @param {string} props.view - The dock view the feedback was submitted from (list / picker / chat).
 * @param {string} [props.assetCorpusId] - The asset the user was chatting about, if any.
 */
export function trackSubmitFeedback(props: {
    feedback: string;
    view: string;
    assetCorpusId?: string;
}) {
    track(AssetChatUserEvents.SubmitFeedback, props);
}

/**
 * trackSendMessage
 * Tracks every message a user sends to the AI chat. The full prompt text is
 * sent as the `message` property so it can be read in PostHog.
 * @param {string} props.message - The full text the user submitted.
 * @param {string} props.assetCorpusId - The asset whose data room the chat is grounded in.
 * @param {string} props.conversationId - Client conversation id, for grouping messages into threads.
 * @param {AssetChatSurface} props.surface - Which chat UI the message was sent from.
 * @param {"typed" | "suggestion"} props.source - Whether the user typed it or clicked a starter question.
 * @param {boolean} props.isFirstMessage - Whether this message started the conversation.
 */
export function trackSendMessage(props: {
    message: string;
    assetCorpusId: string;
    conversationId: string;
    surface: AssetChatSurface;
    source: "typed" | "suggestion";
    isFirstMessage: boolean;
}) {
    track(AssetChatUserEvents.SendMessage, props);
}

/**
 * trackStopGeneration
 * Tracks the event when a user stops an in-flight AI answer.
 */
export function trackStopGeneration(props: {
    assetCorpusId: string;
    conversationId: string;
    surface: AssetChatSurface;
}) {
    track(AssetChatUserEvents.StopGeneration, props);
}

/**
 * trackResponseError
 * Tracks the event when an AI answer fails and the error banner is shown.
 */
export function trackResponseError(props: {
    assetCorpusId: string;
    conversationId: string;
    surface: AssetChatSurface;
}) {
    track(AssetChatUserEvents.ResponseError, props);
}
